import axios from 'axios';
import API_CONFIG from '../config/api';

// API base URL from configuration
const API_URL = API_CONFIG.baseURL;

// Function to get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for Image Report Request
export interface ImageReportRequest {
  _id: string;
  userId: string;
  patientInfo: {
    admissionNumber: string;
    nationalId: string;
    firstName: string;
    lastName: string;
    testDate: string;
  };
  receiptImage: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  adminResponse?: {
    respondedBy?: {
      name: string;
      lastName: string;
    };
    responseDate?: string;
    rejectionReason?: string;
    approvedReportPdf?: string;
    notes?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Interface for creating new request
export interface CreateImageReportRequest {
  patientInfo: {
    admissionNumber: string;
    nationalId: string;
    firstName: string;
    lastName: string;
    testDate: string;
  };
  description?: string;
  receiptImage: File;
}

// Interface for pagination
interface PaginationData {
  currentPage: number;
  total: number;
  totalPages: number;
  limit: number;
}

// Interface for paginated response
interface PaginatedResponse {
  success: boolean;
  message?: string;
  data: ImageReportRequest[];
  pagination: PaginationData;
}

/**
 * Create new image report request
 */
export const createImageReportRequest = async (requestData: CreateImageReportRequest) => {
  try {
    const formData = new FormData();
    
    // Append patient info
    formData.append('patientInfo[admissionNumber]', requestData.patientInfo.admissionNumber);
    formData.append('patientInfo[nationalId]', requestData.patientInfo.nationalId);
    formData.append('patientInfo[firstName]', requestData.patientInfo.firstName);
    formData.append('patientInfo[lastName]', requestData.patientInfo.lastName);
    formData.append('patientInfo[testDate]', requestData.patientInfo.testDate);
    
    // Append description if provided
    if (requestData.description) {
      formData.append('description', requestData.description);
    }
    
    // Append receipt image
    formData.append('receiptImage', requestData.receiptImage);

    const response = await axios.post(
      `${API_URL}/image-reports/create`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get user's image report requests
 */
export const getUserImageReportRequests = async (params: {
  page?: number;
  limit?: number;
} = {}) => {
  try {
    const response = await axios.get(
      `${API_URL}/image-reports/my-requests`,
      {
        params,
        ...getAuthHeaders(),
      }
    );

    return response.data as PaginatedResponse;
  } catch (error) {

    throw error;
  }
};

/**
 * Get single request details
 */
export const getImageReportRequestDetails = async (requestId: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/image-reports/request/${requestId}`,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Download approved report PDF
 */
export const downloadImageReport = async (requestId: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/image-reports/download/${requestId}`,
      {
        ...getAuthHeaders(),
        responseType: 'blob',
      }
    );

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Try to get filename from response headers
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'report.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'فایل با موفقیت دانلود شد' };
  } catch (error) {

    throw error;
  }
};

// Admin functions

/**
 * Get all image report requests (admin only)
 */
export const getAllImageReportRequests = async (params: {
  page?: number;
  limit?: number;
  status?: string;
} = {}) => {
  try {
    const response = await axios.get(
      `${API_URL}/image-reports/admin/all`,
      {
        params,
        ...getAuthHeaders(),
      }
    );

    return response.data as PaginatedResponse;
  } catch (error) {

    throw error;
  }
};

/**
 * Update request status (admin only)
 */
export const updateImageReportRequestStatus = async (
  requestId: string,
  statusData: {
    status: 'approved' | 'rejected';
    rejectionReason?: string;
    notes?: string;
    reportPdf?: File;
  }
) => {
  try {
    const formData = new FormData();
    
    formData.append('status', statusData.status);
    
    if (statusData.rejectionReason) {
      formData.append('rejectionReason', statusData.rejectionReason);
    }
    
    if (statusData.notes) {
      formData.append('notes', statusData.notes);
    }
    
    if (statusData.reportPdf) {
      formData.append('reportPdf', statusData.reportPdf);
    }

    const response = await axios.put(
      `${API_URL}/image-reports/admin/update/${requestId}`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get request statistics (admin only)
 */
export const getImageReportRequestStats = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/image-reports/admin/stats`,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {

    throw error;
  }
};